import { diagnosticIndexStart, diagnosticExtraction } from './preview-diagnostics';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirebaseAuthService } from './firebase-auth.service';
import { AuthInterceptor, RpcAuthenticationError } from './auth.interceptor';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  GetRegionHierarchyResponse,
  PageTextPosition,
  PageTextPositionAvailability,
} from '../../generated.commonjs/api_pb';
import { ArchitecturalPlanPage } from './models/architectural-plan-page.model';
import { HIGHLIGHT_QUERY_PARAMS } from './highlight-regions';
import {
  UNIT_FRAME,
  boundsOfPoints,
  normalizeRotation,
  rotatePermilleYxBox,
  rotateRect,
  rotationDelta,
} from './page-geometry/page-geometry';
import { PdfCacheService } from './pdf-cache.service';
import { PlanReviewerService } from './plan-reviewer.service';

export interface NormalizedTextBox {
  x: number;
  y: number;
  width: number;
  height: number;
  rotationDegrees?: number;
}

export interface TextFocusSelector {
  v: 1;
  exact: string;
  occurrence: number;
  prefix?: string;
  suffix?: string;
  fingerprint?: string;
}

export interface TextSearchMatch {
  id: string;
  fileId: string;
  pageNumber: number;
  matchedText: string;
  context: string;
  boxes: NormalizedTextBox[];
  normalizedBoxes: boolean;
  selector: TextFocusSelector;
}

export type TextSearchAvailability =
  | 'idle'
  | 'searching'
  | 'available'
  | 'pending'
  | 'missing'
  | 'failed'
  | 'stale'
  | 'auth-error';

export interface TextSearchState {
  availability: TextSearchAvailability;
  query: string;
  matches: TextSearchMatch[];
  activeIndex: number;
  message: string;
}

export interface TextUnit {
  text: string;
  box: NormalizedTextBox;
  /** Whether character offsets can be projected proportionally across this box. */
  proportionalGeometry?: boolean;
  /** Stable OCR line identifier used to merge adjacent matched token polygons. */
  lineOrder?: number;
  /**
   * Place of this unit when the page is read block by block within its regions (Issue #3293). The
   * selectable text layer follows it so that a drag stays inside one block; search and matching
   * keep the order of `units`. Absent when the server sent none.
   */
  layoutOrder?: number;
  /** Optional hierarchy attribution used when selected text is sent to chat. */
  regionPath?: string;
  /**
   * The upright rectangle the text covers on the displayed page, as fractions of it (Issue #2445).
   *
   * Present when `box` is drawn turned about its corner, which is how text read from the PDF is
   * described on a rotated page. `box` alone then says where the text starts, not what it covers,
   * so comparing it with another box needs this rectangle instead.
   */
  extent?: NormalizedTextBox;
}

export interface PageTextIndex {
  units: TextUnit[];
  normalizedBoxes: boolean;
  fingerprint: string;
  availability:
    | Exclude<TextSearchAvailability, 'idle' | 'searching' | 'available' | 'stale' | 'auth-error'>
    | 'available';
  /**
   * The server reported this page's text positions as still being read (Issue #3281). It reads them
   * in the background, so asking again a little later returns them.
   */
  positionsPending?: boolean;
}

interface NormalizedText {
  value: string;
  unitAt: number[];
  unitOffsetStart: number[];
  unitOffsetEnd: number[];
}

interface ExtractedPdfText {
  units: TextUnit[];
  normalizedBoxes: boolean;
}

const MIN_NATIVE_TEXT_COVERAGE = 0.8;

/**
 * Characters that join words as printed and that a search reads as a space, on the page and in the
 * query alike, so "3 hour", "3-hour" and "3–hour" find each other (Issue #2691): hyphen-minus,
 * slash, hyphen, non-breaking hyphen, figure dash, en dash, em dash and minus sign. The server's
 * `PlanTextNormalizer.JOINERS` lists the same ones.
 */
const WORD_JOINER = /[\u002d\u002f\u2010-\u2014\u2212]/u;
const WORD_SEPARATORS = /[\s\u002d\u002f\u2010-\u2014\u2212]+/gu;

const EMPTY_STATE: TextSearchState = {
  availability: 'idle',
  query: '',
  matches: [],
  activeIndex: -1,
  message: '',
};

/** Canonical OCR/PDF text search, selector serialization, and Preview focus API (Issue #1744). */
@Injectable({ providedIn: 'root' })
export class TextFocusService {
  private readonly planReviewer = inject(PlanReviewerService);
  private readonly pdfCache = inject(PdfCacheService);
  private readonly router = inject(Router);
  private readonly stateSubject = new BehaviorSubject<TextSearchState>(EMPTY_STATE);
  private readonly focusSubject = new Subject<TextSearchMatch | null>();
  private searchGeneration = 0;
  private authFailureAt = 0;
  private currentAuthUser: object | null = null;
  private readonly auth = inject(FirebaseAuthService);

  static readonly INDEX_CONCURRENCY = 4;
  /**
   * How often, and for how long, a single-page caller asks again for text positions the server is
   * still reading (Issue #3281). One sheet takes the server about 20 seconds to a minute.
   */
  static readonly PENDING_POSITIONS_POLL_MS = 3_000;
  static readonly PENDING_POSITIONS_WAIT_MS = 120_000;
  private context = '';
  private cacheEpoch = 0;
  private readonly completedIndexes = new Map<string, { index: PageTextIndex; expires: number }>();
  private readonly inFlightIndexes = new Map<
    string,
    {
      promise: Promise<PageTextIndex>;
      consumers: Set<() => boolean>;
    }
  >();
  private activeIndexLoads = 0;
  private readonly indexWaiters: (() => void)[] = [];

  constructor() {
    let previousUser: unknown;
    let receivedInitialUser = false;
    this.auth.user$.pipe(takeUntilDestroyed()).subscribe(user => {
      // This singleton can be created lazily when Preview first opens, so its first auth emission
      // only initializes its own empty index state. AuthScopedCacheService is initialized at app
      // startup and owns invalidation of the shared plan/PDF caches across account changes.
      if (!receivedInitialUser) {
        receivedInitialUser = true;
        previousUser = user;
        this.currentAuthUser = user && typeof user === 'object' ? user : null;
        return;
      }
      if (user === previousUser) return;
      if (previousUser && typeof previousUser === 'object') AuthInterceptor.clearAuthFailure(previousUser);
      previousUser = user;
      this.currentAuthUser = user && typeof user === 'object' ? user : null;
      this.authFailureAt = 0;
      this.invalidateIndexes();
    });
    this.planReviewer.textCacheInvalidated$?.pipe(takeUntilDestroyed()).subscribe(() => this.invalidateIndexes());
    this.pdfCache.textCacheInvalidated$?.pipe(takeUntilDestroyed()).subscribe(() => this.invalidateIndexes());
  }

  /** Typed query-free state for opt-in acceptance diagnostics. */
  diagnosticSummary() {
    return {
      generation: this.searchGeneration,
      availability: this.state.availability,
      activeIndex: this.state.activeIndex,
      matches: this.state.matches.map(match => ({
        page: match.pageNumber,
        boxes: match.boxes.map(({ x, y, width, height }) => ({ x, y, width, height })),
      })),
    };
  }

  /** Explicit local-memory reset; never writes saved files or changes routes. */
  resetDiagnosticIndexes(): void {
    this.invalidateIndexes();
    this.pdfCache.clearParsedPdfCache();
  }

  /** Independent offscreen source raster; never supplies the product cache or canvas. */
  async diagnosticReferenceRaster(encoded: string, page: number, scale: number): Promise<string> {
    const reference = await this.pdfCache.getPdfjsLib().getDocument({
      data: Uint8Array.from(atob(encoded), character => character.charCodeAt(0)),
    }).promise;
    try {
      const selected = await reference.getPage(page);
      const viewport = selected.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await selected.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      const pixels = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height).data;
      return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new Uint8Array(pixels))), value =>
        value.toString(16).padStart(2, '0')
      ).join('');
    } finally {
      await reference.destroy();
    }
  }

  private invalidateIndexes(): void {
    this.cacheEpoch++;
    this.completedIndexes.clear();
    this.inFlightIndexes.clear();
    this.clear();
  }

  private selectContext(projectId: string, fileId: string): void {
    const context = JSON.stringify([projectId, fileId]);
    if (context === this.context) return;
    this.context = context;
    this.invalidateIndexes();
  }

  /** Cancel immediately on input changes, before the UI debounce starts another search. */
  cancelSearch(): void {
    this.clear();
  }

  readonly state$ = this.stateSubject.asObservable();
  readonly focus$ = this.focusSubject.asObservable();

  get state(): TextSearchState {
    return this.stateSubject.value;
  }

  async search(projectId: string, fileId: string, query: string): Promise<TextSearchState> {
    this.selectContext(projectId, fileId);
    const trimmed = query.trim();
    if (this.state.availability === 'auth-error') {
      if (Date.now() - this.authFailureAt < 5_000) return this.state;
      const user = this.currentAuthUser ?? this.auth.currentUser;
      if (user) AuthInterceptor.clearAuthFailure(user);
      this.authFailureAt = 0;
    }
    const generation = ++this.searchGeneration;
    if (!trimmed) {
      this.clear();
      return this.state;
    }

    this.stateSubject.next({
      availability: 'searching',
      query: trimmed,
      matches: [],
      activeIndex: -1,
      message: 'Searching document text…',
    });

    try {
      const plan = await this.planReviewer.getArchitecturalPlan(projectId);
      if (generation !== this.searchGeneration) return this.state;
      const pages = (plan?.getPagesList?.() ?? [])
        .filter((page: ArchitecturalPlanPage) => this.pageBelongsToFile(page, fileId))
        .sort(
          (first: ArchitecturalPlanPage, second: ArchitecturalPlanPage) =>
            first.getPageNumber() - second.getPageNumber()
        );
      const indexed: { page: ArchitecturalPlanPage; index: PageTextIndex }[] = [];
      let nextPage = 0;
      let failed = false;
      const current = () => generation === this.searchGeneration && !failed;
      const worker = async () => {
        while (current() && nextPage < pages.length) {
          const page = pages[nextPage++];
          try {
            indexed.push(await this.indexPage(projectId, fileId, page, current));
          } catch (error) {
            failed = true;
            throw error;
          }
        }
      };
      // Wait for all active workers even on failure; none may schedule a successor.
      const results = await Promise.allSettled(
        Array.from({ length: Math.min(TextFocusService.INDEX_CONCURRENCY, pages.length) }, worker)
      );
      const rejection =
        results.find(result => result.status === 'rejected' && result.reason instanceof RpcAuthenticationError) ??
        results.find(result => result.status === 'rejected');
      if (rejection?.status === 'rejected') throw rejection.reason;
      indexed.sort((a, b) => a.page.getPageNumber() - b.page.getPageNumber());
      if (generation !== this.searchGeneration) return this.state;

      const matches = indexed.flatMap(({ page, index }) =>
        this.findMatches(fileId, page.getPageNumber(), index, trimmed)
      );
      const unavailable = indexed.map(({ index }) => index.availability).filter(value => value !== 'available');
      let availability: TextSearchAvailability = 'available';
      let message = matches.length ? `${matches.length} result${matches.length === 1 ? '' : 's'}` : 'No matches found.';
      if (!indexed.length) {
        availability = unavailable.includes('failed')
          ? 'failed'
          : unavailable.includes('pending')
            ? 'pending'
            : 'missing';
        message = this.availabilityMessage(availability);
      } else if (unavailable.length) {
        availability = unavailable.includes('failed')
          ? 'failed'
          : unavailable.includes('pending')
            ? 'pending'
            : 'missing';
        const prefix = matches.length
          ? `${matches.length} result${matches.length === 1 ? '' : 's'} found. `
          : 'No matches found. ';
        message = `${prefix}${this.availabilityMessage(availability)}`;
      }

      const next: TextSearchState = {
        availability,
        query: trimmed,
        matches,
        activeIndex: matches.length ? 0 : -1,
        message,
      };
      this.stateSubject.next(next);
      if (matches.length) await this.focusMatch(projectId, matches[0], false);
      return this.state;
    } catch (error) {
      if (generation !== this.searchGeneration) return this.state;
      const failed: TextSearchState = {
        availability: error instanceof RpcAuthenticationError ? 'auth-error' : 'failed',
        query: trimmed,
        matches: [],
        activeIndex: -1,
        message: error instanceof RpcAuthenticationError ? error.message : this.availabilityMessage('failed'),
      };
      if (error instanceof RpcAuthenticationError) this.authFailureAt = Date.now();
      this.stateSubject.next(failed);
      return failed;
    }
  }

  async move(projectId: string, delta: number): Promise<void> {
    const state = this.state;
    if (!state.matches.length) return;
    const activeIndex = (state.activeIndex + delta + state.matches.length) % state.matches.length;
    this.stateSubject.next({ ...state, activeIndex });
    await this.focusMatch(projectId, state.matches[activeIndex], false);
  }

  /** Focuses a typed result without coupling callers to the keyboard find UI. */
  async focusMatch(projectId: string, match: TextSearchMatch, replaceUrl = false): Promise<void> {
    const generation = this.searchGeneration;
    const selector = TextFocusService.encodeSelector(match.selector);
    const path = `/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(match.fileId)}/pages/${match.pageNumber}/preview`;
    const currentPath = this.router.url.split(/[?#]/, 1)[0];
    // Keep the object highlight when the match is on the page already shown. Every highlight
    // parameter is carried: `highlight` alone would reduce several regions to the first one.
    const currentParams = currentPath === path ? this.router.parseUrl(this.router.url).queryParams : {};
    const highlightParam = HIGHLIGHT_QUERY_PARAMS.map(name => [name, currentParams[name]] as const)
      .filter(([, value]) => typeof value === 'string')
      .map(([name, value]) => `&${name}=${encodeURIComponent(value)}`)
      .join('');
    const url = `${path}?text=${selector}${highlightParam}`;
    await this.router.navigateByUrl(url, { replaceUrl });
    if (generation === this.searchGeneration) this.focusSubject.next(match);
  }

  /** Resolves a caller-supplied selector against one authorized page and emits its boxes. */
  async focusSelector(
    projectId: string,
    fileId: string,
    page: ArchitecturalPlanPage,
    selector: TextFocusSelector,
    pageShown: () => boolean = () => true
  ): Promise<TextSearchMatch | null> {
    this.selectContext(projectId, fileId);
    const generation = ++this.searchGeneration;
    // A caller that moves to another page stops the wait for this page's positions (Issue #3281).
    const current = () => generation === this.searchGeneration && pageShown();
    let index: { page: ArchitecturalPlanPage; index: PageTextIndex };
    try {
      index = await this.indexPageUntilPositioned(projectId, fileId, page, current);
    } catch (error) {
      if (!current()) return null;
      this.stateSubject.next({
        availability: error instanceof RpcAuthenticationError ? 'auth-error' : 'failed',
        query: selector.exact,
        matches: [],
        activeIndex: -1,
        message: error instanceof RpcAuthenticationError ? error.message : this.availabilityMessage('failed'),
      });
      this.focusSubject.next(null);
      return null;
    }
    if (!current()) return null;
    if (index.index.availability !== 'available' && !index.index.units.length) {
      this.stateSubject.next({
        availability: index.index.availability,
        query: selector.exact,
        matches: [],
        activeIndex: -1,
        message: this.availabilityMessage(index.index.availability),
      });
      this.focusSubject.next(null);
      return null;
    }

    const candidates = this.findMatches(fileId, page.getPageNumber(), index.index, selector.exact);
    let match = candidates.find(candidate => candidate.id === this.selectorId(fileId, page.getPageNumber(), selector));
    if (!match && selector.prefix !== undefined) {
      match = candidates.find(
        candidate => candidate.selector.prefix === selector.prefix && candidate.selector.suffix === selector.suffix
      );
    }
    match ??= candidates[selector.occurrence];

    if (!match && index.index.availability !== 'available') {
      this.stateSubject.next({
        availability: index.index.availability,
        query: selector.exact,
        matches: [],
        activeIndex: -1,
        message: this.availabilityMessage(index.index.availability),
      });
      this.focusSubject.next(null);
      return null;
    }

    if (!match || (selector.fingerprint && selector.fingerprint !== index.index.fingerprint)) {
      this.stateSubject.next({
        availability: 'stale',
        query: selector.exact,
        // Stale is terminal until the user explicitly searches again. Keeping
        // candidates here would enable navigation to an unverified location.
        matches: [],
        activeIndex: -1,
        message: 'This text link is stale because the OCR text changed. Search again to create a new link.',
      });
      this.focusSubject.next(null);
      return null;
    }

    if (!this.state.matches.some(candidate => candidate.id === match.id)) {
      this.stateSubject.next({
        availability: index.index.availability,
        query: selector.exact,
        matches: [match],
        activeIndex: 0,
        message:
          index.index.availability === 'available'
            ? '1 result'
            : `1 result found. ${this.availabilityMessage(index.index.availability)}`,
      });
    }
    this.focusSubject.next(match);
    return match;
  }

  clear(): void {
    this.searchGeneration++;
    this.stateSubject.next(EMPTY_STATE);
    this.focusSubject.next(null);
  }

  /**
   * Returns the full indexed text units for a page (Issue #1745), waiting while the server is still
   * reading its text positions for as long as `current` holds (Issue #3281).
   */
  async getPageTextIndex(
    projectId: string,
    fileId: string,
    page: ArchitecturalPlanPage,
    current: () => boolean = () => true
  ): Promise<{ page: ArchitecturalPlanPage; index: PageTextIndex }> {
    this.selectContext(projectId, fileId);
    return this.indexPageUntilPositioned(projectId, fileId, page, current);
  }

  /** Constructs a reproducible TextFocusSelector for a selected text query (Issue #1745). */
  buildSelectorForExact(query: string, index: PageTextIndex, occurrence = 0): TextFocusSelector {
    const normalized = this.normalizeUnits(index.units);
    const needle = this.normalizeQuery(query);
    let from = 0;
    let foundCount = 0;
    let prefix = '';
    let suffix = '';
    while (from <= normalized.value.length - needle.length) {
      const start = normalized.value.indexOf(needle, from);
      if (start === -1) break;
      const end = start + needle.length;
      if (foundCount === occurrence) {
        prefix = normalized.value.slice(Math.max(0, start - 32), start);
        suffix = normalized.value.slice(end, end + 32);
        break;
      }
      foundCount++;
      from = start + Math.max(1, needle.length);
    }
    return {
      v: 1,
      exact: query,
      occurrence,
      prefix: prefix || undefined,
      suffix: suffix || undefined,
      fingerprint: index.fingerprint,
    };
  }

  static encodeSelector(selector: TextFocusSelector): string {
    const json = JSON.stringify(selector);
    if (json.length > 1500) throw new Error('Text selector exceeds the 1,500 character URL limit.');
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach(byte => (binary += String.fromCharCode(byte)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  static decodeSelector(encoded: string): TextFocusSelector | null {
    try {
      if (!encoded || encoded.length > 2000) return null;
      const base64 = encoded
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(Math.ceil(encoded.length / 4) * 4, '=');
      const binary = atob(base64);
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      const value = JSON.parse(new TextDecoder().decode(bytes)) as Partial<TextFocusSelector>;
      if (
        value.v !== 1 ||
        typeof value.exact !== 'string' ||
        !value.exact ||
        value.exact.length > 256 ||
        !Number.isInteger(value.occurrence) ||
        value.occurrence! < 0 ||
        !TextFocusService.isOptionalShortString(value.prefix, 64) ||
        !TextFocusService.isOptionalShortString(value.suffix, 64) ||
        !TextFocusService.isOptionalShortString(value.fingerprint, 128)
      )
        return null;
      return value as TextFocusSelector;
    } catch {
      return null;
    }
  }

  /**
   * Indexes one page and, while the server reports its text positions as still being read, asks
   * again every few seconds until they arrive, `current` stops holding or the wait runs out (Issue
   * #3281). The server answers such a page at once and reads it in the background, so without this
   * a highlight on a page viewed for the first time would never appear. A search over many pages
   * does not wait: it reports the pages as pending.
   */
  private async indexPageUntilPositioned(
    projectId: string,
    fileId: string,
    page: ArchitecturalPlanPage,
    current: () => boolean
  ): Promise<{ page: ArchitecturalPlanPage; index: PageTextIndex }> {
    const deadline = Date.now() + TextFocusService.PENDING_POSITIONS_WAIT_MS;
    let result = await this.indexPage(projectId, fileId, page, current);
    while (result.index.positionsPending && current() && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, TextFocusService.PENDING_POSITIONS_POLL_MS));
      if (!current()) break;
      const key = this.indexKey(projectId, fileId, page, this.cacheEpoch);
      if (this.completedIndexes.get(key)?.index.positionsPending) this.completedIndexes.delete(key);
      result = await this.indexPage(projectId, fileId, page, current);
    }
    return result;
  }

  /** Metadata/ModelStamp changes invalidate completed OCR as well as PDF-derived text. */
  private indexKey(projectId: string, fileId: string, page: ArchitecturalPlanPage, epoch: number): string {
    return JSON.stringify([
      projectId,
      fileId,
      epoch,
      page.getPageFolderPath?.(),
      page.getPageNumber(),
      page.getPageMarkdown?.(),
      page.getModelStamp?.()?.serializeBinary?.().toString(),
    ]);
  }

  private async indexPage(
    projectId: string,
    fileId: string,
    page: ArchitecturalPlanPage,
    current: () => boolean = () => true
  ): Promise<{ page: ArchitecturalPlanPage; index: PageTextIndex }> {
    const epoch = this.cacheEpoch;
    const key = this.indexKey(projectId, fileId, page, epoch);
    const cached = this.completedIndexes.get(key);
    if (cached && cached.expires > Date.now()) return { page, index: cached.index };
    let work = this.inFlightIndexes.get(key);
    if (!work) {
      const queuedGeneration = this.searchGeneration;
      const consumers = new Set<() => boolean>();
      const valid = () => epoch === this.cacheEpoch && [...consumers].some(consumer => consumer());
      const promise = this.withIndexSlot(valid, async () => {
        const result = await this.loadPageIndex(projectId, fileId, page, valid, queuedGeneration, epoch);
        if (valid() && result.index.availability !== 'failed') {
          // Reuse native text while positioned OCR is pending or missing. The
          // availability value stays visible to callers, and the bounded TTL
          // lets a later OCR update replace the incomplete index. Authentication
          // failures are thrown above and are therefore never cached.
          this.completedIndexes.set(key, { index: result.index, expires: Date.now() + 60_000 });
        }
        return result.index;
      });
      work = { promise, consumers };
      this.inFlightIndexes.set(key, work);
    }
    work.consumers.add(current);
    try {
      return { page, index: await work.promise };
    } finally {
      work.consumers.delete(current);
      if (this.inFlightIndexes.get(key) === work) this.inFlightIndexes.delete(key);
    }
  }

  private async withIndexSlot<T>(current: () => boolean, load: () => Promise<T>): Promise<T> {
    // Deferred start lets the first consumer register before validity is checked.
    await Promise.resolve();
    while (this.activeIndexLoads >= TextFocusService.INDEX_CONCURRENCY) {
      await new Promise<void>(resolve => this.indexWaiters.push(resolve));
    }
    if (!current()) {
      this.indexWaiters.shift()?.();
      throw new Error('Search cancelled');
    }
    this.activeIndexLoads++;
    try {
      return await load();
    } finally {
      this.activeIndexLoads--;
      this.indexWaiters.shift()?.();
    }
  }

  private async loadPageIndex(
    projectId: string,
    fileId: string,
    page: ArchitecturalPlanPage,
    current: () => boolean,
    queuedGeneration: number,
    queuedEpoch: number
  ): Promise<{ page: ArchitecturalPlanPage; index: PageTextIndex }> {
    const finishDiagnostic = diagnosticIndexStart(queuedGeneration, queuedEpoch, current());
    try {
      const parsed = await this.pdfCache.getParsedPage(page, { retain: true });
      // The rotation of the PDF these results will be drawn on. OCR tokens from the server are
      // turned to it below when the server says they are expressed for another rotation.
      // Unknown, not upright, when there is no page to read it from: a wrong rotation here would
      // turn tokens that need no turning.
      const loadedRotation = parsed?.page?.rotate;
      const pdfRotation = typeof loadedRotation === 'number' ? normalizeRotation(loadedRotation) : null;
      if (!current()) {
        if (parsed) this.pdfCache.releaseParsedPage(page);
        throw new Error('Search cancelled');
      }
      let digital: ExtractedPdfText;
      try {
        digital = parsed ? await this.extractPdfUnits(parsed.page) : { units: [], normalizedBoxes: false };
      } finally {
        if (parsed) this.pdfCache.releaseParsedPage(page);
      }

      if (!current()) throw new Error('Search cancelled');
      // Legacy projects use /projects/{id}/pages/... storage paths and must
      // omit file_id from backend requests even though their UI route uses 1.
      const apiFileId = page.getPageFolderPath().includes('/files/') ? fileId : undefined;
      let markdown = page.getPageMarkdown();
      if (!markdown) {
        try {
          markdown =
            (
              await this.planReviewer.getPageWithFullContent(projectId, page.getPageNumber(), apiFileId)
            )?.getPageMarkdown() ?? '';
        } catch (error) {
          if (error instanceof RpcAuthenticationError) throw error;
          if (digital.units.length) {
            return { page, index: this.makeIndex(digital.units, digital.normalizedBoxes) };
          }
          throw error;
        }
      }

      if (!current()) throw new Error('Search cancelled');
      if (this.needsPositionedOcr(digital.units, markdown)) {
        try {
          const hierarchy = await this.planReviewer.getRegionHierarchy(projectId, page.getPageNumber(), apiFileId);
          const ocrUnits = this.extractOcrUnits(
            hierarchy.getTextPositionsList(),
            this.ocrTokenTurn(hierarchy, pdfRotation)
          );
          const explicitAvailability = this.explicitOcrAvailability(hierarchy);
          const units = ocrUnits.length
            ? digital.normalizedBoxes
              ? this.combinePositionedUnits(ocrUnits, digital.units)
              : ocrUnits
            : digital.units;
          if (ocrUnits.length || explicitAvailability !== null) {
            const index = this.makeIndex(
              units,
              ocrUnits.length ? true : digital.normalizedBoxes,
              explicitAvailability ?? 'available'
            );
            return { page, index: explicitAvailability === 'pending' ? { ...index, positionsPending: true } : index };
          }
        } catch (error) {
          if (error instanceof RpcAuthenticationError) throw error;
          if (digital.units.length)
            return { page, index: this.makeIndex(digital.units, digital.normalizedBoxes, 'failed') };
          return { page, index: this.makeIndex([], false, 'failed') };
        }
      }

      if (digital.units.length) return { page, index: this.makeIndex(digital.units, digital.normalizedBoxes) };
      const availability = markdown.trim() ? 'missing' : 'pending';
      return {
        page,
        index: {
          units: [],
          normalizedBoxes: false,
          fingerprint: markdown.trim() ? this.hash(markdown) : '',
          availability,
        },
      };
    } catch (error) {
      if (error instanceof RpcAuthenticationError) throw error;
      return { page, index: { units: [], normalizedBoxes: false, fingerprint: '', availability: 'failed' } };
    } finally {
      finishDiagnostic(current());
    }
  }

  private async extractPdfUnits(pdfPage: any): Promise<ExtractedPdfText> {
    diagnosticExtraction();
    const content = await pdfPage.getTextContent();
    const viewport = pdfPage.getViewport({ scale: 1 });
    const viewportWidth = Number(viewport.width);
    const viewportHeight = Number(viewport.height);
    const normalizedBoxes = viewportWidth > 0 && viewportHeight > 0;
    const units = (content.items ?? [])
      .filter((item: any) => typeof item.str === 'string' && item.str.trim())
      .map((item: any) => {
        const transform = viewport.transform;
        const tx = [
          transform[0] * item.transform[0] + transform[2] * item.transform[1],
          transform[1] * item.transform[0] + transform[3] * item.transform[1],
          transform[0] * item.transform[2] + transform[2] * item.transform[3],
          transform[1] * item.transform[2] + transform[3] * item.transform[3],
          transform[0] * item.transform[4] + transform[2] * item.transform[5] + transform[4],
          transform[1] * item.transform[4] + transform[3] * item.transform[5] + transform[5],
        ];
        const height = Math.max(1, Math.hypot(tx[2], tx[3]));
        const directionLength = Math.max(1e-6, Math.hypot(tx[0], tx[1]));
        const x = tx[4] + tx[2];
        const y = tx[5] + tx[3];
        const width = Math.max(1, item.width);
        const direction = { x: tx[0] / directionLength, y: tx[1] / directionLength };
        return {
          text: item.str,
          box: {
            x: normalizedBoxes ? x / viewportWidth : x,
            y: normalizedBoxes ? y / viewportHeight : y,
            width: normalizedBoxes ? width / viewportWidth : width,
            height: normalizedBoxes ? height / viewportHeight : height,
            rotationDegrees: (Math.atan2(direction.y, direction.x) * 180) / Math.PI,
          },
          proportionalGeometry: true,
          ...(normalizedBoxes
            ? {
                extent: TextFocusService.uprightExtent(
                  { x, y },
                  direction,
                  { width, height },
                  {
                    width: viewportWidth,
                    height: viewportHeight,
                  }
                ),
              }
            : {}),
        };
      });
    return { units, normalizedBoxes };
  }

  /**
   * The upright rectangle covered by a run of text drawn along `direction` from `origin` (Issue #2445).
   *
   * Text read from the PDF is described by where it starts, how long and tall it is, and the
   * direction it runs in. On a page displayed turned, that direction is no longer left to right:
   * the run goes down the screen on a page turned 90° clockwise. The rectangle it covers is the
   * bounds of its four corners, returned as fractions of the displayed page.
   *
   * @param origin the corner the text starts at, in viewport pixels
   * @param direction unit vector the text runs along, with y pointing down the screen
   * @param size the run's length and height in viewport pixels
   * @param viewport the displayed page's size in pixels
   */
  private static uprightExtent(
    origin: { x: number; y: number },
    direction: { x: number; y: number },
    size: { width: number; height: number },
    viewport: { width: number; height: number }
  ): NormalizedTextBox {
    const along = { x: direction.x * size.width, y: direction.y * size.width };
    // A quarter turn clockwise of the reading direction points from the top of the text to its baseline.
    const down = { x: -direction.y * size.height, y: direction.x * size.height };
    const corners = [
      origin,
      { x: origin.x + along.x, y: origin.y + along.y },
      { x: origin.x + down.x, y: origin.y + down.y },
      { x: origin.x + along.x + down.x, y: origin.y + along.y + down.y },
    ].map(corner => ({ x: corner.x / viewport.width, y: corner.y / viewport.height }));
    return boundsOfPoints(corners);
  }

  /**
   * The clockwise turn that carries the server's OCR tokens onto the PDF as loaded here (Issue #2445).
   *
   * Tokens are fractions of the displayed page, so they only place text correctly at one rotation.
   * The server states which one. It differs from the PDF in hand when the two were fetched either
   * side of a page rotation, for example a token response still cached from before the turn.
   *
   * The response states the rotation in one of two ways. Tokens from a file that records its own
   * rotation carry `text_position_rotation_degrees`. Tokens from an older file do not, and are by
   * contract expressed for the page rotation the server resolved the response for,
   * `display_rotation_degrees`: the server kept those files in step with the page. Only a response
   * with no geometry at all, from a server that predates the label, leaves the rotation unknown,
   * and then nothing is turned.
   */
  private ocrTokenTurn(hierarchy: GetRegionHierarchyResponse, pdfRotation: number | null): number {
    const geometry = hierarchy.getGeometry?.();
    if (pdfRotation === null || !geometry) return 0;
    const tokenRotation = geometry.hasTextPositionRotationDegrees()
      ? geometry.getTextPositionRotationDegrees()
      : geometry.getDisplayRotationDegrees();
    return rotationDelta(tokenRotation, pdfRotation);
  }

  private extractOcrUnits(positions: PageTextPosition[], clockwiseTurn = 0): TextUnit[] {
    return [...positions]
      .sort((first, second) => first.getReadingOrder() - second.getReadingOrder())
      .flatMap(position => {
        const text = position.getText().trim();
        const storedBox = position.getBoundingBoxList();
        if (!text || storedBox.length !== 4) return [];
        const box = clockwiseTurn ? rotatePermilleYxBox(storedBox, clockwiseTurn) : storedBox;
        const [yMin, xMin, yMax, xMax] = box;
        if (xMin < 0 || yMin < 0 || xMax > 1000 || yMax > 1000 || xMax <= xMin || yMax <= yMin) return [];
        return [
          {
            text,
            box: {
              x: xMin / 1000,
              y: yMin / 1000,
              width: (xMax - xMin) / 1000,
              height: (yMax - yMin) / 1000,
            },
            // The persisted unit is an exact OCR token polygon. Glyph advances
            // within a token must not be inferred.
            proportionalGeometry: false,
            lineOrder: position.getLineOrder(),
            ...(position.getLayoutOrder() ? { layoutOrder: position.getLayoutOrder() } : {}),
          },
        ];
      });
  }

  private needsPositionedOcr(digitalUnits: TextUnit[], markdown: string): boolean {
    if (!digitalUnits.length) return true;
    const expected = this.normalizeString(markdown).replace(/\s/gu, '');
    if (!expected) return false;
    const native = this.normalizeString(digitalUnits.map(unit => unit.text).join(' ')).replace(/\s/gu, '');
    return native.length / expected.length < MIN_NATIVE_TEXT_COVERAGE;
  }

  private combinePositionedUnits(ocrUnits: TextUnit[], digitalUnits: TextUnit[]): TextUnit[] {
    const alignedOcrUnits = this.alignOcrCoordinateFrame(ocrUnits, digitalUnits);
    const uniqueDigitalUnits = digitalUnits.filter(
      digitalUnit => !alignedOcrUnits.some(ocrUnit => this.isDuplicateTextGeometry(digitalUnit, ocrUnit))
    );
    return [...alignedOcrUnits, ...uniqueDigitalUnits];
  }

  /**
   * Legacy v3 artifacts can use the PDF renderer's pre-alignment coordinate frame. Infer the
   * quarter-turn correction only when at least two exact native/OCR anchors strongly agree, so
   * genuinely repeated text at different locations is not collapsed by text alone.
   */
  private alignOcrCoordinateFrame(ocrUnits: TextUnit[], digitalUnits: TextUnit[]): TextUnit[] {
    const candidates = [0, 90, 180, 270].map(rotation => {
      const units = rotation
        ? ocrUnits.map(unit => ({ ...unit, box: this.rotateNormalizedBox(unit.box, rotation) }))
        : ocrUnits;
      const candidatePairs = digitalUnits
        .flatMap((digitalUnit, digitalIndex) =>
          units.flatMap((ocrUnit, ocrIndex) =>
            this.normalizeString(ocrUnit.text) === this.normalizeString(digitalUnit.text)
              ? [{ digitalIndex, ocrIndex, overlap: this.coverageOverlap(digitalUnit, ocrUnit) }]
              : []
          )
        )
        .sort((first, second) => second.overlap - first.overlap);
      const usedDigital = new Set<number>();
      const usedOcr = new Set<number>();
      const overlaps = candidatePairs.flatMap(pair => {
        if (usedDigital.has(pair.digitalIndex) || usedOcr.has(pair.ocrIndex)) return [];
        usedDigital.add(pair.digitalIndex);
        usedOcr.add(pair.ocrIndex);
        return [pair.overlap];
      });
      return {
        rotation,
        units,
        strongAnchors: overlaps.filter(overlap => overlap >= 0.7).length,
        score: overlaps.reduce((sum, overlap) => sum + overlap, 0),
      };
    });
    const identity = candidates[0];
    const best = candidates.reduce((winner, candidate) => (candidate.score > winner.score ? candidate : winner));
    return best.rotation !== 0 && best.strongAnchors >= 2 && best.score > identity.score + 0.5 ? best.units : ocrUnits;
  }

  /** Turns a 0–1 text box clockwise through the shared page-geometry library (Issue #2445). */
  private rotateNormalizedBox(box: NormalizedTextBox, clockwiseDegrees: number): NormalizedTextBox {
    return { ...box, ...rotateRect(box, UNIT_FRAME, clockwiseDegrees) };
  }

  private isDuplicateTextGeometry(first: TextUnit, second: TextUnit): boolean {
    if (this.normalizeString(first.text) !== this.normalizeString(second.text)) return false;
    // Native glyph metrics and OCR polygons differ slightly even when they cover the same visible
    // token. Exact normalized text plus majority overlap of the smaller box is sufficiently strict
    // while tolerating the measured baseline offset in rotated hybrid PDFs.
    return this.coverageOverlap(first, second) >= 0.5;
  }

  /**
   * How much of the smaller of two pieces of text the other covers, from 0 to 1.
   *
   * Compares what each covers on the displayed page. For text drawn turned, that is its `extent`
   * and not its `box`, which only says where the turned text starts (Issue #2445).
   */
  private coverageOverlap(first: TextUnit, second: TextUnit): number {
    return this.geometryOverlap(first.extent ?? first.box, second.extent ?? second.box);
  }

  private geometryOverlap(first: NormalizedTextBox, second: NormalizedTextBox): number {
    const intersectionWidth = Math.max(
      0,
      Math.min(first.x + first.width, second.x + second.width) - Math.max(first.x, second.x)
    );
    const intersectionHeight = Math.max(
      0,
      Math.min(first.y + first.height, second.y + second.height) - Math.max(first.y, second.y)
    );
    const smallerArea = Math.min(first.width * first.height, second.width * second.height);
    return smallerArea > 0 ? (intersectionWidth * intersectionHeight) / smallerArea : 0;
  }

  private explicitOcrAvailability(hierarchy: GetRegionHierarchyResponse): PageTextIndex['availability'] | null {
    switch (hierarchy.getPositionalOcrStatus()) {
      case PageTextPositionAvailability.PAGE_TEXT_POSITION_AVAILABLE:
        return 'available';
      case PageTextPositionAvailability.PAGE_TEXT_POSITION_PENDING:
        return 'pending';
      case PageTextPositionAvailability.PAGE_TEXT_POSITION_MISSING:
        return 'missing';
      case PageTextPositionAvailability.PAGE_TEXT_POSITION_FAILED:
        return 'failed';
      default:
        return null;
    }
  }

  private makeIndex(
    units: TextUnit[],
    normalizedBoxes = false,
    availability: PageTextIndex['availability'] = 'available'
  ): PageTextIndex {
    return {
      units,
      normalizedBoxes,
      fingerprint: this.hash(units.map(unit => unit.text).join('\n')),
      availability,
    };
  }

  private findMatches(fileId: string, pageNumber: number, index: PageTextIndex, query: string): TextSearchMatch[] {
    if (!index.units.length) return [];
    const normalized = this.normalizeUnits(index.units);
    const needle = this.normalizeQuery(query);
    if (!needle) return [];

    const matches: TextSearchMatch[] = [];
    let from = 0;
    while (from <= normalized.value.length - needle.length) {
      const start = normalized.value.indexOf(needle, from);
      if (start === -1) break;
      const end = start + needle.length;
      const unitIds = [...new Set(normalized.unitAt.slice(start, end).filter(unit => unit >= 0))];
      const occurrence = matches.length;
      const prefix = normalized.value.slice(Math.max(0, start - 32), start);
      const suffix = normalized.value.slice(end, end + 32);
      const selector: TextFocusSelector = {
        v: 1,
        exact: query,
        occurrence,
        prefix,
        suffix,
        fingerprint: index.fingerprint,
      };
      const matchedBoxes = unitIds.map(unit => {
        const matchedPositions = normalized.unitAt
          .slice(start, end)
          .map((candidate, offset) => ({ candidate, position: start + offset }))
          .filter(({ candidate }) => candidate === unit)
          .map(({ position }) => position);
        const offsetStart = Math.min(...matchedPositions.map(position => normalized.unitOffsetStart[position]));
        const offsetEnd = Math.max(...matchedPositions.map(position => normalized.unitOffsetEnd[position]));
        const box = index.units[unit].box;
        if (!index.units[unit].proportionalGeometry) return { ...box };
        const angleRadians = ((box.rotationDegrees ?? 0) * Math.PI) / 180;
        return {
          x: box.x + box.width * offsetStart * Math.cos(angleRadians),
          y: box.y + box.width * offsetStart * Math.sin(angleRadians),
          width: Math.max(1e-6, box.width * (offsetEnd - offsetStart)),
          height: box.height,
          rotationDegrees: box.rotationDegrees,
        };
      });
      const boxes = unitIds.every(unit => !index.units[unit].proportionalGeometry)
        ? this.mergeOcrTokenBoxes(
            unitIds.map((unit, position) => ({ box: matchedBoxes[position], lineOrder: index.units[unit].lineOrder }))
          )
        : matchedBoxes;
      matches.push({
        id: this.selectorId(fileId, pageNumber, selector),
        fileId,
        pageNumber,
        matchedText: normalized.value.slice(start, end),
        context: normalized.value.slice(Math.max(0, start - 48), Math.min(normalized.value.length, end + 48)),
        boxes,
        normalizedBoxes: index.normalizedBoxes,
        selector,
      });
      from = start + Math.max(1, needle.length);
    }
    return matches;
  }

  private mergeOcrTokenBoxes(entries: { box: NormalizedTextBox; lineOrder?: number }[]): NormalizedTextBox[] {
    const merged: { box: NormalizedTextBox; lineOrder?: number }[] = [];
    entries.forEach(entry => {
      const previous = merged.at(-1);
      if (!previous || previous.lineOrder === undefined || previous.lineOrder !== entry.lineOrder) {
        merged.push({ box: { ...entry.box }, lineOrder: entry.lineOrder });
        return;
      }
      const minimumX = Math.min(previous.box.x, entry.box.x);
      const minimumY = Math.min(previous.box.y, entry.box.y);
      const maximumX = Math.max(previous.box.x + previous.box.width, entry.box.x + entry.box.width);
      const maximumY = Math.max(previous.box.y + previous.box.height, entry.box.y + entry.box.height);
      previous.box = {
        x: minimumX,
        y: minimumY,
        width: maximumX - minimumX,
        height: maximumY - minimumY,
      };
    });
    return merged.map(entry => entry.box);
  }

  private normalizeUnits(units: TextUnit[]): NormalizedText {
    let value = '';
    const unitAt: number[] = [];
    const unitOffsetStart: number[] = [];
    const unitOffsetEnd: number[] = [];
    units.forEach((unit, unitIndex) => {
      const characters = [...unit.text.normalize('NFKC').toLowerCase()];
      characters.forEach((char, characterIndex) => {
        const offsetStart = characterIndex / characters.length;
        const offsetEnd = (characterIndex + 1) / characters.length;
        // A joiner inside a word, as in "3-hour", becomes a space owned by that word, so a hit on
        // either half still covers the word as printed.
        if (/\s/u.test(char) || WORD_JOINER.test(char)) {
          if (!value.endsWith(' ')) {
            value += ' ';
            unitAt.push(unitIndex);
            unitOffsetStart.push(offsetStart);
            unitOffsetEnd.push(offsetEnd);
          }
        } else {
          value += char;
          unitAt.push(unitIndex);
          unitOffsetStart.push(offsetStart);
          unitOffsetEnd.push(offsetEnd);
        }
      });
      if (unitIndex < units.length - 1 && !value.endsWith(' ')) {
        value += ' ';
        unitAt.push(unitIndex);
        unitOffsetStart.push(1);
        unitOffsetEnd.push(1);
      }
    });
    const firstNonWhitespace = value.search(/\S/u);
    if (firstNonWhitespace === -1) {
      return { value: '', unitAt: [], unitOffsetStart: [], unitOffsetEnd: [] };
    }
    const lastNonWhitespace = value.search(/\s*$/u);
    return {
      value: value.slice(firstNonWhitespace, lastNonWhitespace),
      unitAt: unitAt.slice(firstNonWhitespace, lastNonWhitespace),
      unitOffsetStart: unitOffsetStart.slice(firstNonWhitespace, lastNonWhitespace),
      unitOffsetEnd: unitOffsetEnd.slice(firstNonWhitespace, lastNonWhitespace),
    };
  }

  /** Compares words and counts a page's characters: folded, white space collapsed, joiners kept. */
  private normalizeString(value: string): string {
    return value.normalize('NFKC').toLowerCase().replace(/\s+/gu, ' ').trim();
  }

  /** A query as the search compares it: as `normalizeString`, with joiners read as spaces. */
  private normalizeQuery(value: string): string {
    return value.normalize('NFKC').toLowerCase().replace(WORD_SEPARATORS, ' ').trim();
  }

  private selectorId(fileId: string, pageNumber: number, selector: TextFocusSelector): string {
    return this.hash(
      `${fileId}:${pageNumber}:${this.normalizeString(selector.exact)}:${selector.occurrence}:${selector.prefix ?? ''}:${selector.suffix ?? ''}`
    );
  }

  private hash(value: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
      hash ^= value.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }

  private pageBelongsToFile(page: ArchitecturalPlanPage, fileId: string): boolean {
    const match = page.getPageFolderPath().match(/\/files\/([^/]+)\/pages\//);
    return match ? match[1] === fileId : fileId === '1' || fileId === 'legacy';
  }

  private availabilityMessage(availability: TextSearchAvailability): string {
    switch (availability) {
      case 'pending':
        return 'Document text is still processing. Try again after ingestion completes.';
      case 'missing':
        return 'This document has text, but exact text positions are unavailable. Re-ingest it with OCR positioning enabled.';
      case 'failed':
        return 'Some document text could not be indexed, so search results may be incomplete.';
      default:
        return '';
    }
  }

  private static isOptionalShortString(value: unknown, maximumLength: number): boolean {
    return value === undefined || (typeof value === 'string' && value.length <= maximumLength);
  }
}
